import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';

import { accent, font, neutral, radius, text as textToken } from 'styles/revampTokens';

interface Properties {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the trimmed column name. */
  onCreate: (name: string) => void;
}

/**
 * Names a new table column (a "property" in the API's terms).
 *
 * The "+" in the grid's header corner used to dispatch straight into the
 * secondary header's inline input, which put the field far away from the
 * button that opened it. Asking for the name here keeps the interaction where
 * the user clicked.
 *
 * Deliberately name-only — the same modal shape as AddDimensionModal minus the
 * description, since a column has no description to give.
 */
const AddPropertyModal = ({ isOpen, onClose, onCreate }: Properties) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (isOpen) setName('');
  }, [isOpen]);

  const canCreate = Boolean(name.trim());

  const handleCreate = () => {
    if (!canCreate) return;
    onCreate(name.trim());
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered blockScrollOnMount={false}>
      <ModalOverlay />
      <ModalContent width='412px' maxWidth='90vw' borderRadius={radius.overlay} p='16px'>
        <ModalBody p={0}>
          <Flex direction='column' align='center' gap='20px'>
            <Text
              textAlign='center'
              fontFamily='Comfortaa Bold'
              fontWeight='700'
              fontSize='18px'
              color={textToken.primary}
            >
              Add New Property
            </Text>
            <Flex direction='column' gap='4px' width='100%'>
              <Text
                fontFamily={font.body}
                fontWeight='600'
                fontSize='12px'
                color={textToken.secondary}
              >
                Name
              </Text>
              <Box
                as='input'
                value={name}
                onChange={(event: any) => setName(event.target.value)}
                onKeyDown={(event: any) => {
                  if (event?.key === 'Enter') handleCreate();
                }}
                placeholder='Enter property name here'
                autoFocus
                width='100%'
                bg={neutral.surface}
                border={`1px solid ${neutral.border}`}
                borderRadius={radius.control}
                p='12px'
                fontFamily={font.body}
                fontSize='13px'
                color={textToken.primary}
                outline='none'
                _placeholder={{ color: textToken.muted }}
                _focus={{ borderColor: accent.solid }}
              />
            </Flex>
            <Flex gap='16px' width='100%' height='43px'>
              <Button
                flex={1}
                bg={neutral.surface}
                border={`1px solid ${neutral.border}`}
                borderRadius={radius.control}
                fontFamily={font.body}
                fontWeight='500'
                fontSize='13px'
                color={textToken.primary}
                height='100%'
                transition='background 0.12s ease'
                _hover={{ bg: neutral.surfaceHover }}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                flex={1}
                bg={accent.solid}
                borderRadius={radius.control}
                fontFamily={font.body}
                fontWeight='500'
                fontSize='13px'
                color={textToken.inverse}
                height='100%'
                isDisabled={!canCreate}
                transition='background 0.12s ease'
                _hover={{ bg: accent.hover }}
                onClick={handleCreate}
              >
                Add New Property
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddPropertyModal;
